import { useState } from "react";
import axios from "axios";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { NEED_OPTIONS } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = {
  name: "",
  phone: "",
  email: "",
  condition: "",
  monthly_payment: "",
  down_payment: "",
  trade_in: "",
  vehicle_type: "",
  message: "",
};

const inputClass =
  "w-full bg-white border border-gray-200 rounded-sm px-4 py-3 text-sm text-[#0A0A0A] placeholder:text-gray-400 focus:border-[#C8102E] focus:ring-1 focus:ring-[#C8102E] focus:outline-none transition-all";

export const CarRequestForm = ({ showHeader = true, onSuccess }) => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, form);
      setDone(true);
      setForm(initial);
      toast.success("Request sent — Malcolm will reach out soon.");
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error("Something went wrong. Please try calling or texting Malcolm.");
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="lead-success" className="py-16 text-center">
        <CheckCircle2 size={56} className="mx-auto text-[#C8102E]" />
        <h3 className="mt-6 font-heading text-2xl font-bold uppercase tracking-tight text-[#0A0A0A]">Thank you!</h3>
        <p className="mt-3 text-gray-600">Thanks — Malcolm will review this and reach out with options.</p>
        <button onClick={() => setDone(false)} data-testid="lead-reset-btn" className="mt-6 text-sm font-bold uppercase tracking-wide text-[#C8102E]">
          Send another request
        </button>
      </div>
    );
  }

  return (
    <>
      {showHeader && (
        <>
          <h3 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight uppercase text-[#0A0A0A] leading-[0.95]">
            What can Malcolm help you with?
          </h3>
          <p className="mt-3 text-gray-600">Share a few details and Malcolm will follow up with the best next step.</p>
        </>
      )}
      <form onSubmit={submit} className="mt-8 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input data-testid="lead-name" className={inputClass} placeholder="Name *" value={form.name} onChange={update("name")} />
          <input data-testid="lead-phone" className={inputClass} placeholder="Phone *" value={form.phone} onChange={update("phone")} />
        </div>
        <input data-testid="lead-email" type="email" className={inputClass} placeholder="Email" value={form.email} onChange={update("email")} />
        <select data-testid="lead-condition" className={inputClass} value={form.condition} onChange={update("condition")}>
          <option value="">What do you need help with?</option>
          {NEED_OPTIONS.map((o) => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input data-testid="lead-monthly" className={inputClass} placeholder="Desired monthly payment" value={form.monthly_payment} onChange={update("monthly_payment")} />
          <select data-testid="lead-trade" className={inputClass} value={form.trade_in} onChange={update("trade_in")}>
            <option value="">Do you have a trade-in?</option>
            <option value="Yes">Yes</option>
            <option value="No">No</option>
            <option value="Maybe">Maybe</option>
          </select>
        </div>
        <textarea data-testid="lead-message" rows={4} className={inputClass} placeholder="Message" value={form.message} onChange={update("message")} />
        <button type="submit" disabled={loading} data-testid="lead-submit-btn" className="w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-sm font-bold uppercase tracking-wide text-white bg-[#C8102E] rounded-sm hover:bg-[#A00D25] transition-all hover:-translate-y-0.5 shadow-lg shadow-[#C8102E]/30 disabled:opacity-60">
          {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
          {loading ? "Sending..." : "Send My Request to Malcolm"}
        </button>
      </form>
    </>
  );
};
